// Simplified Human Design bodygraph: 9 centers + channel lines. Gold when defined.
const LAYOUT = [
  { key: "Head", x: 100, y: 24, shape: "tri" },
  { key: "Ajna", x: 100, y: 74, shape: "tri-down" },
  { key: "Throat", x: 100, y: 128, shape: "sq" },
  { key: "G", x: 100, y: 190, shape: "diamond" },
  { key: "Heart", x: 156, y: 200, shape: "sq-sm" },
  { key: "Spleen", x: 34, y: 250, shape: "tri-r" },
  { key: "Solar Plexus", x: 166, y: 250, shape: "tri-l" },
  { key: "Sacral", x: 100, y: 258, shape: "sq" },
  { key: "Root", x: 100, y: 320, shape: "sq" },
];

const POS = Object.fromEntries(LAYOUT.map((c) => [c.key, c]));

const GATE_TO_CENTER = {
  64: "Head", 61: "Head", 63: "Head",
  47: "Ajna", 24: "Ajna", 4: "Ajna", 17: "Ajna", 43: "Ajna", 11: "Ajna",
  62: "Throat", 23: "Throat", 56: "Throat", 16: "Throat", 20: "Throat", 31: "Throat",
  8: "Throat", 33: "Throat", 35: "Throat", 12: "Throat", 45: "Throat",
  1: "G", 13: "G", 25: "G", 46: "G", 2: "G", 15: "G", 10: "G", 7: "G",
  21: "Heart", 40: "Heart", 26: "Heart", 51: "Heart",
  48: "Spleen", 57: "Spleen", 44: "Spleen", 50: "Spleen", 32: "Spleen", 28: "Spleen", 18: "Spleen",
  34: "Sacral", 5: "Sacral", 14: "Sacral", 29: "Sacral", 59: "Sacral", 9: "Sacral", 3: "Sacral", 42: "Sacral", 27: "Sacral",
  6: "Solar Plexus", 37: "Solar Plexus", 22: "Solar Plexus", 36: "Solar Plexus", 30: "Solar Plexus", 55: "Solar Plexus", 49: "Solar Plexus",
  58: "Root", 38: "Root", 54: "Root", 53: "Root", 60: "Root", 52: "Root", 19: "Root", 39: "Root", 41: "Root",
};

// All 36 channels as [gate1, gate2]
const ALL_CHANNELS = [
  [1, 8], [2, 14], [3, 60], [4, 63], [5, 15], [6, 59], [7, 31], [9, 52],
  [10, 20], [10, 34], [10, 57], [11, 56], [12, 22], [13, 33], [16, 48],
  [17, 62], [18, 58], [19, 49], [20, 34], [20, 57], [21, 45], [23, 43],
  [24, 61], [25, 51], [26, 44], [27, 50], [28, 38], [29, 46], [30, 41],
  [32, 54], [34, 57], [35, 36], [37, 40], [39, 55], [42, 53], [47, 64],
];

// small perpendicular offset so parallel channels between the same two centers don't overlap
function offsetPoints(a, b, i, total) {
  const dx = b.x - a.x, dy = b.y - a.y;
  const len = Math.hypot(dx, dy) || 1;
  const nx = -dy / len, ny = dx / len;
  const spread = 5;
  const shift = (i - (total - 1) / 2) * spread;
  return { x1: a.x + nx * shift, y1: a.y + ny * shift, x2: b.x + nx * shift, y2: b.y + ny * shift };
}

function Shape({ c, defined }) {
  const fill = defined ? "hsl(43 50% 50%)" : "transparent";
  const stroke = defined ? "hsl(43 50% 60%)" : "hsl(240 10% 28%)";
  const common = { fill, stroke, strokeWidth: 1.5 };
  const s = 30;
  switch (c.shape) {
    case "tri":
      return <polygon points={`${c.x},${c.y - s / 2} ${c.x - s / 2},${c.y + s / 2} ${c.x + s / 2},${c.y + s / 2}`} {...common} />;
    case "tri-down":
      return <polygon points={`${c.x - s / 2},${c.y - s / 2} ${c.x + s / 2},${c.y - s / 2} ${c.x},${c.y + s / 2}`} {...common} />;
    case "tri-r":
      return <polygon points={`${c.x - s / 2},${c.y - s / 2} ${c.x + s / 2},${c.y} ${c.x - s / 2},${c.y + s / 2}`} {...common} />;
    case "tri-l":
      return <polygon points={`${c.x + s / 2},${c.y - s / 2} ${c.x - s / 2},${c.y} ${c.x + s / 2},${c.y + s / 2}`} {...common} />;
    case "diamond":
      return <polygon points={`${c.x},${c.y - s / 2} ${c.x + s / 2},${c.y} ${c.x},${c.y + s / 2} ${c.x - s / 2},${c.y}`} {...common} />;
    case "sq-sm":
      return <rect x={c.x - 11} y={c.y - 11} width={22} height={22} rx={3} {...common} />;
    default:
      return <rect x={c.x - s / 2} y={c.y - s / 2} width={s} height={s} rx={4} {...common} />;
  }
}

export default function BodyGraph({ defined = [], channels = [] }) {
  const set = new Set(defined);
  const definedSet = new Set((channels || []).map((c) => c.split("-").map(Number).sort((a, b) => a - b).join("-")));

  // group channels by the center-pair they connect so we can fan out parallels
  const groups = {};
  ALL_CHANNELS.forEach(([g1, g2]) => {
    const c1 = GATE_TO_CENTER[g1], c2 = GATE_TO_CENTER[g2];
    if (!c1 || !c2 || c1 === c2) return;
    const pairKey = [c1, c2].sort().join("|");
    (groups[pairKey] ||= []).push({ g1, g2, c1, c2 });
  });

  const lines = [];
  Object.values(groups).forEach((chans) => {
    chans.forEach((ch, i) => {
      const a = POS[ch.c1], b = POS[ch.c2];
      const key = [ch.g1, ch.g2].sort((x, y) => x - y).join("-");
      const isDefined = definedSet.has(key);
      const pts = offsetPoints(a, b, i, chans.length);
      lines.push(
        <line key={`${ch.g1}-${ch.g2}`} {...pts} data-testid={isDefined ? `channel-line-${key}` : undefined}
          stroke={isDefined ? "hsl(43 60% 55%)" : "hsl(240 10% 22%)"}
          strokeWidth={isDefined ? 2.5 : 1} strokeLinecap="round"
          opacity={isDefined ? 0.95 : 0.5} />
      );
    });
  });

  return (
    <svg viewBox="0 0 200 360" className="w-full max-w-[220px] mx-auto" data-testid="bodygraph">
      <g data-testid="channel-lines">{lines}</g>
      {LAYOUT.map((c) => <Shape key={c.key} c={c} defined={set.has(c.key)} />)}
    </svg>
  );
}
